import { useEffect, useState } from 'react'
import { fetchPerpPositions } from '../../api/perp'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import type { PerpPosition } from '../../types/perp'
import type { StageAllocation } from '../../types/strategy'
import JupiterPerpsPanel from './JupiterPerpsPanel'

function usd(v: string) {
  return '$' + parseFloat(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function usdSigned(v: string) {
  const n = parseFloat(v)
  const formatted = '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
  return n >= 0 ? '+' + formatted : '-' + formatted
}

const COLS = 'grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr]'

export default function PerpetualsPanel({ onPanelUsd, wsData, hedgeStage }: { onPanelUsd?: (key: 'perp', usd: number) => void; wsData?: PerpPosition[] | null; hedgeStage?: StageAllocation | null } = {}) {
  const { portfolioId } = useAuth()
  const [positions, setPositions] = useState<PerpPosition[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showManage, setShowManage] = useState(false)
  const shimmerRef = useShimmerOnUpdate(wsData)

  // Initial data: use batch snapshot from parent when available, otherwise fall back to
  // individual REST fetch. wsData intentionally excluded from deps.
  useEffect(() => {
    if (!portfolioId) return
    if (wsData != null) { setLoading(false); return }
    setLoading(true)
    fetchPerpPositions(portfolioId)
      .then(data => { setPositions(data); setError(null) })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [portfolioId])  

  // Live update from WebSocket push
  useEffect(() => {
    if (wsData == null) return
    setPositions(wsData)
    setLoading(false)
  }, [wsData])

  useEffect(() => {
    if (positions) onPanelUsd?.('perp', positions.reduce((s, p) => s + parseFloat(p.sizeUsd), 0))
  }, [positions])

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-fringe">
        <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center text-amber-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <polyline points="2 17 8 11 13 16 22 7" />
            <polyline points="16 7 22 7 22 13" />
          </svg>
        </div>
        <span className="text-sm font-semibold text-ink">Perpetuals</span>
        {positions && (
          <span className="ml-1 text-xs bg-yellow-400/20 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">
            {positions.length}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {positions && positions.length > 0 && (() => {
            const totalNotional = positions.reduce((s, p) => s + parseFloat(p.sizeUsd), 0)
            const totalPnl = positions.reduce((s, p) => s + parseFloat(p.unrealizedPnlUsd), 0)
            const hasPnl = positions.some(p => parseFloat(p.unrealizedPnlUsd) !== 0)
            const pnlColor = totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'
            return (
              <>
                <span className="text-sm font-semibold text-ink">{usd(String(totalNotional))}</span>
                {hasPnl && (
                  <span className={`text-xs font-medium ${pnlColor}`}>
                    {usdSigned(String(totalPnl))}
                  </span>
                )}
              </>
            )
          })()}
          <button
            onClick={() => setShowManage(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-yellow-400/20 hover:bg-yellow-400/40 text-amber-400 hover:text-yellow-300 text-[11px] font-medium transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Manage
          </button>
        </div>
      </div>

      {/* Body */}
      {error && (
        <p className="text-accent-red text-xs p-4">{error}</p>
      )}
      {loading && (
        <div className="animate-pulse p-4 space-y-2">
          <div className="h-3 w-full bg-surface rounded"/>
          <div className="h-3 w-3/4 bg-surface rounded"/>
        </div>
      )}
      {!loading && positions && positions.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-8">No perpetual positions found</p>
      )}
      {!loading && positions && positions.length > 0 && (
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-surface">
          <div className="min-w-[560px]">
          {/* Column headers */}
          <div className={`grid ${COLS} gap-x-3 px-4 sm:px-5 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-fringe`}>
            <span>Protocol</span>
            <span>Market</span>
            <span>Side</span>
            <span className="text-right">Size</span>
            <span className="text-right">Collateral</span>
            <span className="text-right">Leverage</span>
            <span className="text-right">PnL</span>
          </div>

          <div className="max-h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-surface scrollbar-track-transparent divide-y divide-white/5">
            {positions.map((pos, i) => (
              <PositionRow key={pos.provider + pos.market + pos.side + i} data={pos} />
            ))}
          </div>
          </div>
        </div>
      )}
      {showManage && (
        <JupiterPerpsPanel
          positions={positions ?? []}
          hedgeStage={hedgeStage ?? undefined}
          onClose={() => setShowManage(false)}
        />
      )}
    </div>
  )
}

function PositionRow({ data }: { data: PerpPosition }) {
  const pnl = parseFloat(data.unrealizedPnlUsd)
  const pnlColor = pnl >= 0 ? 'text-accent-green' : 'text-accent-red'

  return (
    <div className={`grid ${COLS} gap-x-3 px-4 sm:px-5 py-2.5 sm:py-3 items-center hover:bg-surface-hover/30 transition-colors text-sm`}>
      {/* Protocol */}
      <span className="font-medium text-ink capitalize">{data.provider}</span>

      {/* Market */}
      <span className="text-ink-soft">{data.market}</span>

      {/* Side */}
      <span>
        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
          data.side === 'long'
            ? 'bg-accent-green/20 text-accent-green'
            : 'bg-accent-red/20 text-accent-red'
        }`}>
          {data.side}
        </span>
      </span>

      {/* Size */}
      <span className="text-right font-semibold text-ink">{usd(data.sizeUsd)}</span>

      {/* Collateral */}
      <span className="text-right text-ink-soft">{usd(data.collateralUsd)}</span>

      {/* Leverage */}
      <span className="text-right text-accent-blue font-medium">
        {parseFloat(data.leverage) > 0 ? `${parseFloat(data.leverage).toFixed(1)}×` : '—'}
      </span>

      {/* PnL */}
      <span className={`text-right font-medium ${pnlColor}`}>
        {pnl !== 0 ? usdSigned(data.unrealizedPnlUsd) : '—'}
      </span>
    </div>
  )
}
